import {
  AuthError,
  autoSignIn,
  confirmSignUp,
  resendSignUpCode,
} from "aws-amplify/auth";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";

import {
  ConfirmEmailFormData,
  confirmEmailSchema,
} from "../types/confirm-email.schema";

type VerifyEmailFormProps = {
  email: string;
};

export function VerifyEmailForm({ email }: VerifyEmailFormProps) {
  const [countdown, setCountdown] = useState(30);
  const [isResendActive, setIsResendActive] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (isResendActive) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown((prevCount) => {
        if (prevCount <= 1) {
          clearInterval(timer);
          setIsResendActive(true);
          return 0;
        }
        return prevCount - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isResendActive]);

  const handleResend = async () => {
    setCountdown(30);
    setIsResendActive(false);

    try {
      await resendSignUpCode({ username: email.toLowerCase().trim() });
    } catch (error) {
      if (error instanceof AuthError) {
        form.setError("code", {
          message: error.message,
        });
      } else {
        console.error("Unexpected error:", error);
        form.setError("code", {
          message: "An unexpected error occurred. Please try again later.",
        });
      }
    }
  };

  const form = useForm<ConfirmEmailFormData>({
    resolver: zodResolver(confirmEmailSchema),
    defaultValues: {
      code: "",
    },
  });

  async function onSubmit(data: ConfirmEmailFormData) {
    try {
      const response = await confirmSignUp({
        username: email.toLowerCase().trim(),
        confirmationCode: data.code,
      });

      if (response.nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
        await autoSignIn();

        navigate("/");
        return;
      }

      if (response.isSignUpComplete) {
        navigate("/sign-in?confirmed=true");
        return;
      }
    } catch (error) {
      Sentry.captureException(error);
      if (error instanceof AuthError) {
        Sentry.captureMessage("UserRegistrationFailed", {
          extra: {
            error,
          },
        });
        if (error.name === "EmptyConfirmSignUpUsername") {
          navigate("/");

          return;
        }

        form.setError("code", {
          message: error.message,
        });
      } else {
        console.error("Unexpected error:", error);
        form.setError("code", {
          message: "An unexpected error occurred. Please try again later.",
        });
      }
    }
  }
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel isRequired>Enter Code</FormLabel>
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  disabled={form.formState.isSubmitting}
                  onComplete={(code) => {
                    form.setValue("code", code);
                    form.handleSubmit(onSubmit)();
                  }}
                  autoFocus
                  autoComplete="one-time-code"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              <FormDescription>
                {isResendActive ? (
                  <p>
                    Didn't receive a code?{" "}
                    <Button
                      onClick={handleResend}
                      variant="link"
                      type="button"
                      className="h-auto p-0"
                    >
                      Resend
                    </Button>
                  </p>
                ) : (
                  `Didn't receive a code? Resend (${countdown})`
                )}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex w-full">
          <Button
            type="submit"
            isLoading={form.formState.isSubmitting}
            className="ml-auto"
            isAnimated
          >
            Verify
          </Button>
        </div>
      </form>
    </Form>
  );
}
